import './ShareTargetModal.scss';

import React, { FC, useCallback, useMemo, useState } from 'react';
import { IonButton, IonCol, IonGrid, IonIcon, IonLabel, IonRow } from '@ionic/react';
import { JobMessages } from '../../intl/JobMessages';
import { business, person } from 'ionicons/icons';
import { useIntl } from 'react-intl';
import { GenericMessages } from '../../intl/GenericMessages';
import { AppModal } from '../reusable-components/app-modal/AppModal';
import NavCard from '../nav-card/NavCard';
import { EmailGuestShare, JobLogPermission } from '../../models/jobLog';
import type { Permission } from '../../models/bundle';
import { DqText } from '../dq-text';

export type ShareTarget = 'family-org' | 'guest';

export interface ShareTargetModalProps {
  canShareJobReport: boolean;
  onSubmit: (target: ShareTarget) => void;
  onUnshare?: (removed: string[]) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
  /* All permissions and emailGuestShares passed in will be displayed. Parent should pass only unexpired ones. */
  unexpiredPermissions: Permission[] | JobLogPermission[] | undefined;
  unexpiredEmailGuestShares: EmailGuestShare[] | undefined;
}

export interface UnshareModalRowProps {
  isShared: boolean;
  rowData: UnshareModalRowData;
  toggle: (key: string) => void;
  disabled: boolean;
  showButton?: boolean;
}

export interface UnshareModalRowData {
  key: string;
  displayName: string;
}

export const UnshareModalRow: React.FC<UnshareModalRowProps> = props => {
  const { formatMessage } = useIntl();
  const { isShared, rowData, toggle } = props;

  const hideButton = props.showButton ?? true;
  return (
    <IonRow className="ion-align-items-center">
      <DqText style={{ flex: 1 }}>{rowData.displayName}</DqText>
      {hideButton && (
        <IonButton
          fill="clear"
          color={isShared ? 'danger' : 'success'}
          onClick={() => toggle(rowData.key)}
          disabled={props.disabled}
        >
          {formatMessage(
            isShared ? JobMessages.unshareJobSelectForUnsharing : JobMessages.unshareJobDeselectForUnsharing,
          )}
        </IonButton>
      )}
    </IonRow>
  );
};

/**
 * Initial share modal that shows any existing shares and provides options to
 * create new shares.
 *
 * There are separate modal components for sharing with an org and via email.
 */
export const ShareTargetModal: FC<ShareTargetModalProps> = props => {
  const { formatMessage } = useIntl();
  const { canShareJobReport, unexpiredEmailGuestShares, onClose, onUnshare, unexpiredPermissions } = props;
  const [unshared, setUnshared] = useState<string[]>([]);
  const [isLoading, setLoading] = useState(false);

  // Create modal row data for all org shares followed by all email shares.
  // Note that key must be unique for UnshareModalRow. In the case of an org share, the organization id (principal)
  // fills that bill.  OTOH, it is possible there may be more than one share with the same email address so email
  // address alone is not unique. However, the combination of email address and expiresOn timestamp will be unique.
  // Further, in order for the share to be removed by the backend, the share must be identified uniquely, so this
  // email address and timestamp are also needed for that reason.  To keep things simple, the key is a string and so
  // for an email share, the key will be the email address followed by a space, followed by the timestamp. The unshare
  // function can use this to distinguish between the unshare of an org share vs an email share.
  const rowData: UnshareModalRowData[] =
    unexpiredPermissions?.map(p => {
      return { key: p.principal, displayName: p.principalName };
    }) ?? [];

  // Add email guest shares to rowData.
  for (const e of unexpiredEmailGuestShares ?? []) {
    if (e.expiresOn > Date.now()) {
      const key = `${e.emailAddress} ${e.expiresOn}`;
      rowData.push({ key, displayName: e.emailAddress });
    }
  }

  const handleSubmit = useCallback(async () => {
    if (!onUnshare) return () => {};

    try {
      setLoading(true);
      await onUnshare(unshared);
      setUnshared([]); // clear the unshared list for next time its opened
    } finally {
      setLoading(false);
    }
  }, [onUnshare, unshared]);

  const handleCancel = useCallback(async () => {
    try {
      setLoading(true);
      onClose();
      setUnshared([]); // clear the unshared list for next time its opened
    } finally {
      setLoading(false);
    }
  }, [onClose]);

  const removeSelectedButton = useMemo(() => {
    if (!onUnshare) return undefined;

    return {
      label: formatMessage(JobMessages.unshareJobConfirmationButtonText),
      onClick: handleSubmit,
      disabled: unshared.length === 0 || isLoading,
    };
  }, [formatMessage, handleSubmit, isLoading, onUnshare, unshared.length]);

  const cancelButton = useMemo(() => {
    return {
      label: formatMessage(GenericMessages.cancelButtonLabel),
      onClick: handleCancel,
      disabled: isLoading,
    };
  }, [formatMessage, handleCancel, isLoading]);

  const toggle = useCallback((key: string) => {
    setUnshared(prev => {
      if (prev.includes(key)) {
        return prev.filter(id => id !== key);
      } else {
        return [...prev, key];
      }
    });
  }, []);

  return (
    <AppModal
      className="share-target-modal"
      isOpen={props.isOpen}
      title={formatMessage(JobMessages.manageSharingTitle)}
      onClose={handleCancel}
      primaryButton={removeSelectedButton}
      secondaryButton={cancelButton}
    >
      <IonGrid>
        <IonRow className="ion-padding-vertical select-container">
          {canShareJobReport && (
            <IonCol onClick={() => props.onSubmit('family-org')}>
              <NavCard
                title={formatMessage(JobMessages.addOrgTitle)}
                subtitle={formatMessage(JobMessages.addOrgSubtitle)}
                icon={<IonIcon className="icon-large" ios={business} md={business} color="primary" />}
              />
            </IonCol>
          )}
          {canShareJobReport && (
            <IonCol onClick={() => props.onSubmit('guest')}>
              <NavCard
                title={formatMessage(JobMessages.addGuestTitle)}
                subtitle={formatMessage(JobMessages.addGuestSubtitle)}
                icon={<IonIcon className="icon-large" ios={person} md={person} color="primary" />}
              />
            </IonCol>
          )}
        </IonRow>
        <IonRow className="ion-padding-vertical">
          <IonLabel>{formatMessage(JobMessages.unshareJobDescription)}</IonLabel>
        </IonRow>
        {rowData.map(d => {
          return (
            <UnshareModalRow
              key={d.key}
              rowData={d}
              isShared={!unshared.includes(d.key)}
              toggle={toggle}
              disabled={isLoading}
              showButton={!!onUnshare}
            />
          );
        })}
      </IonGrid>
    </AppModal>
  );
};
